import { useContext, useEffect, useRef, useState } from "react";
import { EntryContext } from "../../../../../contexts/EntryContext";
import * as THREE from "three";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useInput } from "../../../../../hooks/useInput";
import { useOnChange } from "../../../../../hooks/useOnChange";
import commonUtils from "../../../../../../script/utils/commonUtils";
import { Vector3D } from "../../../../../../script/lod2";
import { useOverview3dCalcAreaObjects } from "./useOverview3dCalcAreaObjects";
import { useValidation } from "../../../../../hooks/useValidation";
import intl from "react-intl-universal";

const baseAreaColor = "#06b6d4";
const highlightedAreaColor = "#90e459";

export const useOverview3dDisplaySettings = layerName => {
    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;
    const { overviewSetting, overview3dSetting } = entry;
    const [highlightedArea, setHighlightedArea] = useState();
    const [calcAreasObjects, setCalcAreasObjects, calcAreasObjectsRef] = useOverview3dCalcAreaObjects();
    const cameraSettings = useRef(overview3dSetting?.cameraSettings ?? {});
    const outdoorResultVisibleInput = useInput(layerName + "-outdoor-result-visible", overview3dSetting?.outdoorResultVisible ?? false);
    const indoorResultVisibleInput = useInput(layerName + "-indoor-result-visible", overview3dSetting?.indoorResultVisible ?? true);
    const plansObject = useRef();
    const planVisibleInput = useInput(layerName + "-plan-result-visible", overview3dSetting?.planVisible ?? true);
    const attenuationGlobalInputActive = overviewSetting?.fieldType !== "PROTECTION_AREA_BLMSCHV" && overviewSetting?.fieldLimit;
    const attenuationGlobalInput = useInput(layerName + "-apply-global-dumping", attenuationGlobalInputActive && (overview3dSetting?.attenuationGlobal ?? false));
    const [buildingsObject, setBuildingsObject] = useState();
    const latitudeInput = useInput(layerName + "-lattitude", String(overview3dSetting?.latitude ?? ""));
    const longitudeInput = useInput(layerName + "-longitude", String(overview3dSetting?.longitude ?? ""));
    const buildingsElevationInput = useInput(layerName + "-buildings-elevation",overview3dSetting?.elevation);
    const radiusInput = useInput(layerName + "-radius", overview3dSetting?.radius);
    const buildingsVisibleInput = useInput(layerName + "-buildings-visible", overview3dSetting?.buildingsVisible ?? false);
    const [buildingsVisibleInputEnabled, setBuildingsVisibleInputEnabled] = useState(Boolean(latitudeInput.value) && Boolean(longitudeInput.value) && Boolean(buildingsElevationInput.value && Boolean(radiusInput.value) && radiusInput.value <= 200));
    const buildingsTranslationXInput = useInput(layerName + "-buildings-trans-x",overview3dSetting?.buildingsTranslationX);
    const buildingsTranslationYInput = useInput(layerName + "-buildings-trans-y",overview3dSetting?.buildingsTranslationY);
    const buildingsTranslationZInput = useInput(layerName + "-buildings-trans-z",overview3dSetting?.buildingsTranslationZ);

    const validationOptions = { globalErrorMessage: intl.get("entry.plan.overview3d.title") };
    useValidation(value => commonUtils.isBlank(value) || value <= 200, [radiusInput], validationOptions);

    const [triangles, setTriangles] = useState([]);
    const produceTriangles = useDebounce(() => {
        const buildingTranslation = new Vector3D((buildingsTranslationXInput.value ?? 0), (buildingsTranslationYInput.value ?? 0), (buildingsTranslationZInput.value ?? 0));
        const trianglesArray = [
            (buildingsObject?.triangles ?? []).map(vertex => vertex.add(buildingTranslation)),
            ...(calcAreasObjects?.map(object => object.calcAreaObject.triangles)) ?? [],
        ].flat();
        if(trianglesArray.length % 3 !== 0) {
            throw new Error("Overview3dAntennasList - Wrong number of vertices");
        }
        const triangles = [];
        for(let i = 0; i < trianglesArray.length; i += 3) {
            triangles.push([trianglesArray[i], trianglesArray[i+1], trianglesArray[i+2]]);
        }
        setTriangles(triangles);
    }, 3000);
    useEffect(produceTriangles,[
        buildingsObject,
        calcAreasObjects,
        buildingsTranslationXInput.value,
        buildingsTranslationYInput.value,
        buildingsTranslationZInput.value
    ]);

    const saveSettings = () => {
        entry.overview3dSetting = {
            ...entry.overview3dSetting,
            cameraSettings: cameraSettings.current,
            outdoorResultVisible: outdoorResultVisibleInput.value,
            indoorResultVisible: indoorResultVisibleInput.value,
            planVisible: planVisibleInput.value,
            attenuationGlobal: attenuationGlobalInput.value,
            latitude: latitudeInput.value,
            longitude: longitudeInput.value,
            elevation: buildingsElevationInput.value,
            radius: radiusInput.value,
            buildingsVisible: buildingsVisibleInput.value,
            buildingsTranslationX: buildingsTranslationXInput.value,
            buildingsTranslationY: buildingsTranslationYInput.value,
            buildingsTranslationZ: buildingsTranslationZInput.value,
        };
    };

    const setVisibility = (plot3d, calcArea, baseHeight, value) => {
        const calcAreaObject = calcAreasObjectsRef.current.find(cao => cao.calcArea === calcArea);
        if(calcAreaObject && value === false) {
            setCalcAreasObjects(calcAreasObjectsRef.current.filter(cao => cao.calcArea !== calcArea));
            plot3d.scene.remove(calcAreaObject.calcAreaObject.group);
            plot3d.render();
        } else if (!calcAreaObject && value === true) {
            const calcAreaObject = plot3d.addCalcArea(calcArea, baseAreaColor, baseHeight);
            setCalcAreasObjects([...calcAreasObjectsRef.current, {calcArea, calcAreaObject}]);
        }

        calcArea.overview3dVisible = value;
        //entryContext.setEntry(entry);
    };

    const onAreaItemHighlight = (plot3d, calcArea) => {
        const calcAreaObject = calcAreasObjects.find(cao => cao.calcArea === calcArea);
        if(calcAreaObject) {
            const material = new THREE.MeshBasicMaterial({
                color: new THREE.Color(highlightedAreaColor),
                side: THREE.DoubleSide,
            });
            material.transparent = true;
            material.opacity = 0.3;
            calcAreaObject.calcAreaObject.fill.material = material;
            plot3d.render();
            setHighlightedArea(calcArea);
        }
    };

    const onAreaItemHighlightEnd = (plot3d, calcArea) => {
        const calcAreaObject = calcAreasObjects.find(cao => cao.calcArea === calcArea);
        if(calcAreaObject) {
            const material = new THREE.MeshBasicMaterial({
                color: new THREE.Color(baseAreaColor),
                side: THREE.DoubleSide,
            });
            material.transparent = true;
            material.opacity = 0.3;
            calcAreaObject.calcAreaObject.fill.material = material;
            plot3d.render();
        }
        setHighlightedArea();
    };

    const isAreaHighlighted = calcArea => calcArea === highlightedArea;

    const initSetVisibility = (plot3d, calcAreas, baseHeight) => {
        const visibleAreas = calcAreas.filter(ca => ca.overview3dVisible ?? true);
        return visibleAreas.map(ca => setVisibility(plot3d, ca, baseHeight, true));
    };

    const onCameraViewChanged = useDebounce(
        plot3d => {
            const {camera, controls} = plot3d;
            cameraSettings.current = {
                position: {
                    x: camera.position.x,
                    y: camera.position.y,
                    z: camera.position.z,
                },
                rotation: {
                    x: camera.rotation.x,
                    y: camera.rotation.y,
                    z: camera.rotation.z,
                },
                target: {
                    x: controls.getTarget().x,
                    y: controls.getTarget().y,
                    z: controls.getTarget().z,
                },
                zoom: camera.zoom,
            };
            saveSettings();
        },
        500,
    );

    const setBuildingsTranslation = commonUtils.throttle(
        buildingsObject => {
            if(buildingsObject) {
                buildingsObject.group.position.set(
                    buildingsTranslationXInput.value ?? 0,
                    buildingsTranslationYInput.value ?? 0,
                    buildingsTranslationZInput.value ?? 0,
                );
            }
        //plot3d.render();
        },
        100,
    );

    const setBuildings = (plot3d, buildings, buildingsColor) => {
        if(buildings) {
            const buildingsObj = plot3d.addBuildings(buildings, buildingsColor);
            setBuildingsObject(buildingsObj);
            setBuildingsTranslation(buildingsObj);
        } else {
            if(buildingsObject) {
                plot3d.scene.remove(buildingsObject.group);
                setBuildingsObject(undefined);
            }
        }
    };

    const setPlans = async (plot3d, plans, planUrls, height) => {
        if(plans && planUrls) {
            const plansObj = await plot3d.addPlans(plans, planUrls, height);
            plansObject.current = plansObj;
        } else {
            if(plansObject.current) {
                plot3d.scene.remove(plansObject.current);
                plansObject.current = undefined;
            }
        }
    };

    useOnChange(() => {
        if([latitudeInput.value, longitudeInput.value, buildingsElevationInput.value, radiusInput.value]
            .find(value => commonUtils.isBlank(value)) === undefined
            && radiusInput.value <= 200) {
            setBuildingsVisibleInputEnabled(true);
        } else {
            setBuildingsVisibleInputEnabled(false);
            buildingsVisibleInput.set(false);
            buildingsVisibleInput.value = false;
        }
    }, [latitudeInput, longitudeInput, buildingsElevationInput, radiusInput]);

    useOnChange(() => {
        saveSettings();
    }, [outdoorResultVisibleInput,
        indoorResultVisibleInput,
        planVisibleInput,
        attenuationGlobalInput,
        longitudeInput,
        latitudeInput,
        buildingsElevationInput,
        radiusInput,
        buildingsVisibleInput,
        buildingsTranslationXInput,
        buildingsTranslationYInput,
        buildingsTranslationZInput,
        attenuationGlobalInput,
    ]);

    return {
        cameraSettings: cameraSettings.current,
        outdoorResultVisibleInput,
        indoorResultVisibleInput,
        planVisibleInput,
        attenuationGlobalInputActive,
        attenuationGlobalInput,
        latitudeInput,
        longitudeInput,
        buildingsElevationInput,
        radiusInput,
        buildingsVisibleInput,
        buildingsVisibleInputEnabled,
        buildingsTranslationXInput,
        buildingsTranslationYInput,
        buildingsTranslationZInput,
        buildingsObject,
        triangles,
        initSetVisibility,
        setVisibility,
        onAreaItemHighlight,
        onAreaItemHighlightEnd,
        isAreaHighlighted,
        onCameraViewChanged,
        setBuildings,
        setBuildingsTranslation,
        setPlans,
    };
};